/* eslint-disable @typescript-eslint/no-use-before-define */
import React from 'react';
import { withStyles, Grid, Typography, Paper } from '@material-ui/core';
import styles, { StyledComponent } from '../../../../../styles/styles';
import { LabeledValue } from '../../../../../controls/labeled-value';
import { connect } from 'react-redux';
import { State } from '../../../../../redux/root-reducer';
import { FormDetails, getFormDetails } from '../form-details-reducer';
import { getDisplayDate } from '../../../../../shared-functions';
import { OpenModal, openModalAction } from '../../../../../form-modal/actions';
import { UpdateFormDetails, updateFormDetailsAction } from '../actions/update-form-details';
import { getClaim } from '../../../../../claim';
import { ClaimType } from '../../../../../claim/claim-types';
import { Nullable } from '../../../../../shared-types';

interface BaseProps {
    formDetails?: FormDetails;
    disableEditButton?: boolean;
    updateFormDetails?: UpdateFormDetails;
    claimType?: Nullable<ClaimType>;
}

interface Props extends BaseProps, StyledComponent {
    readonly openModal: OpenModal;
}

const employmentView: React.FC<Props> = ({
    classes,
    openModal,
    formDetails,
    updateFormDetails,
    disableEditButton,
    claimType,
}: Props) => {
    const employment = formDetails?.employment;
    const details = employment?.employmentDetails;

    return (
        <>
            <Grid container justify="space-between" className={classes.marginTop2}>
                <Grid container item xs={10} alignItems="center">
                    <Typography style={{ padding: 10 }} className={classes.heading} variant="body1">
                        Employment Details
                    </Typography>
                </Grid>
            </Grid>

            <Paper className={classes.paper}>
                {/* EMPLOYMENT DETAILS */}
                <Grid container spacing={2}>
                    {claimType !== ClaimType.Retrenchment && (
                        <>
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="SA income tax reference number"
                                    value={details?.saIncomeTaxReferenceNumber}
                                />
                            </Grid>
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="Tax office (Which city do you pay your taxes in?)"
                                    value={details?.taxOffice}
                                />
                            </Grid>
                        </>
                    )}
                    <Grid item xs={4} lg={3}>
                        <LabeledValue
                            label="Name of current or previous employer"
                            value={
                                details?.nameOfCurrentOrPreviousEmployer || details?.nameOfEmployer
                            }
                        />
                    </Grid>

                    <Grid item xs={4} lg={3}>
                        <LabeledValue label="Address" value={details?.address} />
                    </Grid>

                    <Grid item xs={4} lg={3}>
                        <LabeledValue label="Postal Code" value={details?.postalCode} />
                    </Grid>

                    <Grid item xs={4} lg={3}>
                        <LabeledValue
                            label="Date employed from"
                            value={getDisplayDate(details?.dateEmployedFrom)}
                        />
                    </Grid>

                    <Grid item xs={4} lg={3}>
                        <LabeledValue
                            label="Date employed to"
                            value={getDisplayDate(details?.dateEmployedTo)}
                        />
                    </Grid>
                    {claimType === ClaimType.Retrenchment && (
                        <>
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="Effective date of retrenchment"
                                    value={getDisplayDate(details?.effectiveDateOfRetrenchment)}
                                />
                            </Grid>
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="Please provide employer contract basis"
                                    value={details?.employerContractBasis}
                                />
                            </Grid>
                        </>
                    )}
                    {claimType !== ClaimType.Retrenchment && (
                        <>
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="What was your full time occupation before your current disability?"
                                    value={
                                        details?.whatWasYourFullTimeOccupationBeforeYourCurrentDisability
                                    }
                                />
                            </Grid>
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="Which of the following items describes the work that you do?"
                                    value={details?.whichOfTheFollowingItemsDescribesTheWorkThatYouDo?.join(
                                        ', '
                                    )}
                                />
                            </Grid>
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="Please provide an accurate description of the exact nature and duties of your full time occupation"
                                    value={
                                        details?.pleaseProvideAnAccurateDescriptionOfTheExactNatureAndDutiesOfYourFullTimeOccupation
                                    }
                                />
                            </Grid>
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="Is there any hazard associated with your occupation (e.g mining, asbestos, handling of explosives)"
                                    value={details?.isThereAnyHazardAssociatedWithYourOccupation}
                                />
                            </Grid>
                        </>
                    )}
                    {details?.pleaseProvideDetailsRelatingToHazardsAssociatedWithYourOccupation &&
                        claimType !== ClaimType.Retrenchment && (
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="Please provide details relating to hazards associated with your occupation"
                                    value={
                                        details?.pleaseProvideDetailsRelatingToHazardsAssociatedWithYourOccupation
                                    }
                                />
                            </Grid>
                        )}
                    {claimType !== ClaimType.Retrenchment && (
                        <Grid item xs={4} lg={3}>
                            <LabeledValue
                                label="Do you travel beyond the borders of South Africa in the course of your duties"
                                value={
                                    details?.doYouTravelBeyondTheBordersOfSouthAfricaInTheCourseOfYourDuties
                                }
                            />
                        </Grid>
                    )}
                    {details?.pleaseProvideDetailsOfCountryThatYouTravelToAndTheNatureOfYourDuties &&
                        claimType !== ClaimType.Retrenchment && (
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="Please provide details of country that you travel to and the nature of your duties"
                                    value={
                                        details?.pleaseProvideDetailsOfCountryThatYouTravelToAndTheNatureOfYourDuties
                                    }
                                />
                            </Grid>
                        )}
                    {claimType !== ClaimType.Retrenchment && (
                        <>
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="What date did you stop working?"
                                    value={getDisplayDate(details?.whatDateDidYouStopWorking)}
                                />
                            </Grid>
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="When do you expect to return to work?"
                                    value={getDisplayDate(details?.whenDoYouExpectToReturnToWork)}
                                />
                            </Grid>
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="Please provide details of your wellbeing"
                                    value={details?.pleaseProvideDetailsOfYourWellBeing}
                                />
                            </Grid>
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="Have you been offered or have enquired on any alternative occupation for remuneration by your employer?"
                                    value={
                                        details?.haveYouBeenOfferedOrHaveEnquiredOnAnyAlternativeOccupationForRenumerationByYourEmployer
                                    }
                                />
                            </Grid>
                        </>
                    )}
                    {details?.haveYouBeenOfferedOrHaveEnquiredOnAnyAlternativeOccupationForRenumerationByYourEmployer ===
                        'Yes' && (
                        <Grid item xs={4} lg={3}>
                            <LabeledValue
                                label="What type of occupation will it be?"
                                value={details?.whaTypeOfOccupationWillItBe}
                            />
                        </Grid>
                    )}
                </Grid>
            </Paper>
        </>
    );
};

const mapStateToProps = (state: State, ownProps: BaseProps) => ({
    formDetails: ownProps.formDetails ?? getFormDetails(state),
    disableEditButton: ownProps.disableEditButton ?? false,
    updateFormDetails: ownProps.updateFormDetails ?? updateFormDetailsAction,
    claimType: getClaim(state).claimType,
});

const mapDispatchToProps = {
    openModal: openModalAction,
    updateFormDetails: updateFormDetailsAction,
};

export const EmploymentView = connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles)(employmentView));
